import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useCallback, useState } from 'react';

import Button from '../../../library/inputs/Button';
import CalendarEventTemplateListTable from './CalendarEventTemplateListTable';
import CalendarEventTemplateSelectInput from '../../../library/inputs/CalendarEventTemplateSelectInput';
import Flash from '../../../library/utils/Flash';
import LoadingSpinner from '../../../library/utils/LoadingSpinner';
import Section from '../../../library/layout/Section';
import SelectBaseTemplateModal from '../../../library/data-display/SelectBaseTemplateModal';
import TextInput from '../../../library/inputs/TextInput';
import useSyncStateWithQuery from '../../../../hooks/use-sync-state-with-query';
import { StyledHeaderActions } from './styles';
import { useCalendarEventTemplates } from '../../../../hooks/queries/calendar-event-templates';

import type { ChangeEvent } from 'react';
import { correctPath } from 'libraries/gem';

const CalendarEventTemplateList = () => {
  const [search, querySearch, setSearch] = useSyncStateWithQuery<string>('q', '', { delay: 200 });
  const [showArchived, , setShowArchived] = useSyncStateWithQuery<string>('show_archived', 'false');
  const [pageNumber, queryPageNumber, setPageNumber] = useSyncStateWithQuery<string>('page', '1');

  const [isSelectBaseTemplateModalOpen, setIsSelectBaseTemplateModalOpen] = useState<boolean>(false);

  const {
    data: calendarEventTemplates,
    error,
    isFetching,
  } = useCalendarEventTemplates({
    archived: showArchived === 'true',
    name: querySearch, // This has to be querySearch so it doesn't make a lot of requests when you type fast.
    limit: 10,
    offset: 10 * (parseInt(queryPageNumber, 10) - 1),
  });

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    if (parseInt(pageNumber, 10) > 1) {
      setPageNumber('1', { method: 'replace' });
    }
  }, [pageNumber]);

  const handleShowArchivedChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setShowArchived(JSON.stringify(e.target.checked));
  }, []);

  const handlePageNumberChange = useCallback((value: number) => {
    setPageNumber(value.toString());
  }, []);

  return (
    <div className="calendar-event-template-list-container">
      <Helmet>
        <title>Calendar Event Templates | Gem Scheduling</title>
      </Helmet>
      <Section
        headerActions={
          <StyledHeaderActions>
            <Button
              color="gem-outline"
              iconLeft={<FontAwesomeIcon icon={faPlus} />}
              onClick={() => setIsSelectBaseTemplateModalOpen(true)}
              size="small"
              value="New template from base"
            />
            <Link to={correctPath('/app/calendar-event-templates/create')}>
              <Button
                color="gem-blue"
                iconLeft={<FontAwesomeIcon icon={faPlus} />}
                size="small"
                value="New template"
              />
            </Link>
          </StyledHeaderActions>
        }
        title="Calendar event templates"
      >
        <SelectBaseTemplateModal
          isOpen={isSelectBaseTemplateModalOpen}
          onToggle={() => setIsSelectBaseTemplateModalOpen(!isSelectBaseTemplateModalOpen)}
          redirectPath={correctPath('/app/calendar-event-templates/create')}
          resourceName="calendar event template"
          selectInputComponent={CalendarEventTemplateSelectInput}
        />
        <div className="filters-container">
          <TextInput
            id="text-input-calendar-event-template-filter"
            isAutofocus
            label="Search"
            leftIcon={isFetching ? <LoadingSpinner /> : <FontAwesomeIcon icon={faSearch} />}
            onChange={handleSearchChange}
            placeholder="Calendar event template name"
            value={search}
          />
        </div>
        <Flash
          message={error?.message}
          showFlash={Boolean(error)}
          type="danger"
        />
        {calendarEventTemplates && (
          <CalendarEventTemplateListTable
            calendarEventTemplates={calendarEventTemplates.calendar_event_templates}
            onPageNumberChange={handlePageNumberChange}
            onShowArchivedChange={handleShowArchivedChange}
            pageNumber={parseInt(pageNumber, 10)}
            showArchived={showArchived === 'true'}
            totalCount={calendarEventTemplates.total}
          />
        )}
      </Section>
    </div>
  );
};

export default CalendarEventTemplateList;
